import React, { useCallback, useMemo } from 'react';
import {
  View,
  Text,
  Pressable,
  Modal,
  Animated,
  useWindowDimensions,
} from 'react-native';
import { FlashList, ListRenderItemInfo } from '@shopify/flash-list';

import { FLASHLIST_ANDROID_OVERRIDE_PROPS } from '../../../src/shared/constants/flashList.constants';
import { useTheme } from '../../../src/theme/useTheme';
import { CountryFlag } from '../CountryFlag/CountryFlag';
import { futuresTopScorerCaption } from './futuresCardDisplay.helper';
import type { PickerType } from './FuturesCard.constants';
import { FUTURES_CARD_COPY } from './FuturesCard.constants';
import { SoccerKit } from '../../ui/SoccerKit';
import { kitMetaFromScorerOption } from './futuresCardDisplay.helper';
import { getFuturesPickerModalStyles } from './FuturesPickerModal.styles';
import type {
  WorldCupFuturesParticipantTeam,
  WorldCupFuturesTopScorerOption,
  WorldCupFuturesStats,
} from '../../../src/worldcup/helpers/worldcupPicksData.helper';
import { getTeamKitConfig } from '../../../helpers/teamKit.helper';

interface FuturesPickerModalProps {
  activePicker: PickerType | null;
  isSaving: boolean;
  draftWinner: string | null;
  draftScorer: string | null;
  modalTranslateY: Animated.Value;
  pickerTitle: string;
  participantTeams: readonly WorldCupFuturesParticipantTeam[];
  topScorerOptions: readonly WorldCupFuturesTopScorerOption[];
  topScorerOtherId: string | undefined;
  futuresStats: WorldCupFuturesStats | null;
  /**
   * How a winner option is drawn. `'flag'` (the default, and the World Cup's) shows the
   * country flag with the kit as a fallback; `'kit'` shows the kit outright, for a club
   * competition where a national flag would be wrong (specs/champions-league AC-15.9).
   */
  winnerVisual?: 'flag' | 'kit';
  onClose: () => void;
  onSelectWinner: (teamId: string) => void;
  onSelectScorer: (scorerId: string) => void;
}

function sortByPct<T extends { odd?: number }>(
  items: readonly T[],
  pcts: Record<string, number> | undefined,
  getId: (item: T) => string,
): T[] {
  return [...items].sort((a, b) => {
    const pctA = pcts?.[getId(a)] ?? 0;
    const pctB = pcts?.[getId(b)] ?? 0;
    if (pctB !== pctA) return pctB - pctA;
    if (a.odd != null && b.odd != null) return a.odd - b.odd;
    if (a.odd != null) return -1;
    if (b.odd != null) return 1;
    return 0;
  });
}

export const FuturesPickerModal: React.FC<FuturesPickerModalProps> = ({
  activePicker,
  isSaving,
  draftWinner,
  draftScorer,
  modalTranslateY,
  pickerTitle,
  participantTeams,
  topScorerOptions,
  topScorerOtherId,
  futuresStats,
  winnerVisual = 'flag',
  onClose,
  onSelectWinner,
  onSelectScorer,
}) => {
  const theme = useTheme();
  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const styles = useMemo(
    () => getFuturesPickerModalStyles(theme, windowWidth, windowHeight),
    [theme, windowWidth, windowHeight],
  );

  const sortedTeams = useMemo(
    () => sortByPct(participantTeams, futuresStats?.winnerPcts, (t) => t.id),
    [participantTeams, futuresStats?.winnerPcts],
  );
  const sortedScorers = useMemo(
    () => sortByPct(topScorerOptions, futuresStats?.scorerPcts, (o) => o.id),
    [topScorerOptions, futuresStats?.scorerPcts],
  );

  const extraData = useMemo(
    () => ({ draftWinner, draftScorer, isSaving, futuresStats }),
    [draftWinner, draftScorer, isSaving, futuresStats],
  );

  const renderOddBadge = useCallback(
    (odd: number | undefined) => {
      if (odd == null) return null;
      return (
        <View style={styles.oddBadge}>
          <Text style={styles.oddBadgeText}>{odd.toFixed(2)}x</Text>
        </View>
      );
    },
    [styles],
  );

  const renderPickBar = useCallback(
    (pct: number, isSelected: boolean) => (
      <View style={styles.pickBarTrack}>
        <View style={styles.pickBarOuter}>
          <View
            style={[
              styles.pickBarFill,
              // data-driven width — not a static style
              { width: `${pct}%` as `${number}%` },
              isSelected && styles.pickBarFillSelected,
            ]}
          />
        </View>
        <Text style={[styles.pickPctText, isSelected && styles.pickPctTextSelected]}>
          {pct}%
        </Text>
      </View>
    ),
    [styles],
  );

  const renderWinnerTeam = useCallback(
    ({ item: team }: ListRenderItemInfo<WorldCupFuturesParticipantTeam>) => {
      const isSelected = draftWinner === team.id;
      const code = team.countryCode && team.countryCode !== 'xx' ? team.countryCode : null;
      const winnerKitFallback = getTeamKitConfig(team.name);
      const pct = futuresStats?.winnerPcts?.[team.id] ?? 0;
      return (
        <Pressable
          onPress={() => onSelectWinner(team.id)}
          disabled={isSaving}
          style={({ pressed }) => [
            styles.optionRow,
            isSelected && styles.optionRowSelected,
            pressed && styles.optionRowPressed,
            isSaving && styles.optionRowDisabled,
          ]}
        >
          <View style={winnerVisual === 'kit' ? styles.optionKitWrap : styles.optionFlagWrap}>
            {winnerVisual === 'kit' ? (
              <SoccerKit
                size={36}
                primary={winnerKitFallback.primary}
                secondary={winnerKitFallback.secondary}
                type={winnerKitFallback.type}
              />
            ) : (
              <CountryFlag
                countryCode={code ?? ''}
                kitFallback={winnerKitFallback}
                accessibilityLabel={team.name}
              />
            )}
          </View>
          <View style={styles.optionContent}>
            <View style={styles.optionTopRow}>
              <Text
                style={[styles.optionName, isSelected && styles.optionNameSelected]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {team.name}
              </Text>
              {renderOddBadge(team.odd)}
            </View>
            {renderPickBar(pct, isSelected)}
          </View>
        </Pressable>
      );
    },
    [draftWinner, isSaving, onSelectWinner, styles, futuresStats, renderOddBadge, renderPickBar, winnerVisual],
  );

  const renderScorerRow = useCallback(
    ({ item: option }: ListRenderItemInfo<WorldCupFuturesTopScorerOption>) => {
      const isSelected = draftScorer === option.id;
      const meta = kitMetaFromScorerOption(option);
      const pct = futuresStats?.scorerPcts?.[option.id] ?? 0;
      return (
        <Pressable
          onPress={() => onSelectScorer(option.id)}
          disabled={isSaving}
          style={({ pressed }) => [
            styles.optionRow,
            isSelected && styles.optionRowSelected,
            pressed && styles.optionRowPressed,
            isSaving && styles.optionRowDisabled,
          ]}
        >
          <View style={styles.optionKitWrap}>
            <SoccerKit
              size={36}
              primary={meta?.kitColor ?? '#6B7280'}
              secondary={meta?.trimColor ?? '#9CA3AF'}
              number={meta?.number}
            />
          </View>
          <View style={styles.optionContent}>
            <View style={styles.optionTopRow}>
              <Text
                style={[styles.optionName, isSelected && styles.optionNameSelected]}
                numberOfLines={1}
                ellipsizeMode="tail"
              >
                {futuresTopScorerCaption(option.id, option.label)}
              </Text>
              {renderOddBadge(option.odd)}
            </View>
            {renderPickBar(pct, isSelected)}
          </View>
        </Pressable>
      );
    },
    [draftScorer, isSaving, onSelectScorer, styles, futuresStats, renderOddBadge, renderPickBar],
  );

  const scorerOtherFooter = useMemo(() => {
    if (!topScorerOtherId) return null;
    const isSelected = draftScorer === topScorerOtherId;
    const pct = futuresStats?.scorerPcts?.[topScorerOtherId] ?? 0;
    return (
      <Pressable
        onPress={() => onSelectScorer(topScorerOtherId)}
        disabled={isSaving}
        style={({ pressed }) => [
          styles.optionRow,
          styles.optionRowOtherFooter,
          isSelected && styles.optionRowSelected,
          pressed && styles.optionRowPressed,
          isSaving && styles.optionRowDisabled,
        ]}
      >
        <View style={styles.optionKitWrap}>
          <SoccerKit size={36} primary="#6B7280" secondary="#9CA3AF" />
        </View>
        <View style={styles.optionContent}>
          <View style={styles.optionTopRow}>
            <Text
              style={[styles.optionName, isSelected && styles.optionNameSelected]}
              numberOfLines={1}
            >
              {FUTURES_CARD_COPY.PICK_OTHER_PLAYER_LABEL}
            </Text>
          </View>
          {renderPickBar(pct, isSelected)}
        </View>
      </Pressable>
    );
  }, [draftScorer, isSaving, onSelectScorer, styles, topScorerOtherId, futuresStats, renderPickBar]);

  return (
    <Modal visible={activePicker !== null} transparent animationType="none" onRequestClose={onClose} statusBarTranslucent>
      <View style={styles.modalRoot}>
        <Pressable style={styles.modalBackdrop} onPress={onClose} accessibilityRole="button" />
        <Animated.View
          style={[
            styles.modalSheet,
            isSaving && styles.modalSheetSaving,
            { transform: [{ translateY: modalTranslateY }] },
          ]}
        >
          <View style={styles.modalHandle} />
          <View style={styles.modalHeader}>
            <View style={styles.modalTitleWrap}>
              <Text style={styles.modalTitle} numberOfLines={2} ellipsizeMode="tail">
                {pickerTitle}
              </Text>
            </View>
            <Pressable
              style={styles.modalCloseBtn}
              onPress={onClose}
              disabled={isSaving}
              accessibilityRole="button"
              accessibilityLabel="Close"
            >
              <Text style={styles.modalCloseBtnText}>✕</Text>
            </Pressable>
          </View>

          {activePicker === 'winner' ? (
            <View style={styles.modalFlashListWrap}>
              <FlashList
                overrideProps={FLASHLIST_ANDROID_OVERRIDE_PROPS}
                data={sortedTeams}
                keyExtractor={(team) => team.id}
                renderItem={renderWinnerTeam}
                extraData={extraData}
                style={styles.modalFlashList}
                contentContainerStyle={styles.modalList}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                drawDistance={300}
              />
            </View>
          ) : null}

          {activePicker === 'scorer' ? (
            <View style={styles.modalFlashListWrap}>
              <FlashList
                overrideProps={FLASHLIST_ANDROID_OVERRIDE_PROPS}
                data={sortedScorers}
                keyExtractor={(o) => o.id}
                renderItem={renderScorerRow}
                extraData={extraData}
                ListFooterComponent={scorerOtherFooter}
                style={styles.modalFlashList}
                contentContainerStyle={styles.modalList}
                showsVerticalScrollIndicator={false}
                keyboardShouldPersistTaps="handled"
                drawDistance={300}
              />
            </View>
          ) : null}
        </Animated.View>
      </View>
    </Modal>
  );
};
